import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  AGE_GROUPS,
  AVATAR_PARTS,
  NICKNAME_MAX,
  checkNickname,
  formatTag,
  handleOf,
  mayDeclare,
  randomAvatar,
  type AgeGroup,
  type Avatar,
  type AvatarPart,
} from '@il-colpo/shared/account';
import { COSMETICS } from '@il-colpo/meta/progress';
import { AvatarFigure } from '../../account/AvatarFigure.tsx';
import { getAccountStore, useAccount } from '../../account/accountStore.ts';
import { useToastStore } from '../../state/toastStore.ts';
import { ownedCosmetics } from '../meta/CollectionScreen.tsx';
import { useMetaGate, useProgress } from '../meta/common.tsx';
import { Button, Panel, ScreenFrame, SegmentedControl, TextField } from '../../ui/index.ts';
import styles from './Account.module.css';
import { FormError, Page, useAccountSession, useAction, useDateFormat } from './common.tsx';

const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

const PART_ORDER: readonly AvatarPart[] = ['hat', 'mask', 'coat', 'rim', 'suit'];

const AGE_LABEL: Record<AgeGroup, 'age.under14' | 'age.from14' | 'age.adult'> = {
  under_14: 'age.under14',
  '14_to_17': 'age.from14',
  '18_plus': 'age.adult',
};

function NicknameForm() {
  const { t } = useTranslation('account');
  const profile = useAccount((s) => s.profile);
  const { date } = useDateFormat();
  const [nickname, setNickname] = useState(profile?.nickname ?? '');
  const [now] = useState(() => Date.now());
  const { pending, error, run } = useAction();

  if (!profile) return null;
  const checked = checkNickname(nickname);
  const problem =
    !checked.ok && nickname.length > 0 ? t(`profile.nicknameProblem.${checked.problem}`) : null;
  const unchanged = checked.ok && checked.nickname === profile.nickname;
  const waitUntil = profile.nicknameChangedAt
    ? new Date(profile.nicknameChangedAt).getTime() + COOLDOWN_MS
    : 0;
  const cooling = waitUntil > now;

  return (
    <Panel title={t('profile.nickname')}>
      <form
        className={styles['form']}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void run(() => getAccountStore().getState().updateProfile({ nickname })).then((r) => {
            if (r.ok) useToastStore.getState().push(t('profile.saved'), 'success');
          });
        }}
      >
        <TextField
          label={t('profile.nickname')}
          value={nickname}
          maxLength={NICKNAME_MAX + 4}
          autoComplete="nickname"
          onChange={setNickname}
          hint={t('profile.nicknameHint', {
            example: `${nickname.trim() || 'Volpe'}#${formatTag(7)}`,
          })}
          error={problem}
        />
        <p className={styles['note']}>
          {t('profile.handle', { handle: handleOf(profile.nickname, profile.tag) })}
        </p>
        {cooling ? (
          <p className={styles['note']}>{t('profile.cooldown', { date: date(waitUntil) })}</p>
        ) : null}
        <FormError code={error} />
        <div>
          <Button type="submit" loading={pending} disabled={!checked.ok || unchanged || cooling}>
            {t('profile.save')}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function AvatarEditor() {
  const { t } = useTranslation('account');
  const profile = useAccount((s) => s.profile);
  const [draft, setDraft] = useState<Avatar | null>(profile?.avatar ?? null);
  const { pending, error, run } = useAction();
  const gate = useMetaGate();
  const progress = useProgress(gate === 'ready');
  const owned = progress.data ? ownedCosmetics(progress.data) : null;

  if (!profile || !draft) return null;
  // What the player may wear: what is free, what they have earned, and what they already wear.
  const offered = (part: AvatarPart, value: string): boolean => {
    const def = COSMETICS.find((c) => c.part === part && c.value === value);
    if (!def) return true;
    return owned?.has(def.id) === true || draft[part] === value;
  };
  const changed = JSON.stringify(draft) !== JSON.stringify(profile.avatar);

  return (
    <Panel title={t('profile.avatarTitle')}>
      <div className={styles['stack']}>
        <div className={styles['profileTop']}>
          <AvatarFigure
            avatar={draft}
            size="7.5rem"
            label={t('profile.avatarLabel', { name: profile.nickname })}
          />
          <Button
            variant="secondary"
            onClick={() => {
              setDraft(randomAvatar(`${Date.now()}-${Math.random()}`));
            }}
          >
            {t('profile.avatarRandom')}
          </Button>
        </div>
        {PART_ORDER.map((part) => (
          <div
            key={part}
            className={styles['partRow']}
            role="group"
            aria-label={t(`profile.parts.${part}`)}
          >
            <strong>{t(`profile.parts.${part}`)}</strong>
            <div className={styles['partOptions']}>
              {(AVATAR_PARTS[part] as readonly string[])
                .filter((value) => offered(part, value))
                .map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={styles['chip']}
                    aria-pressed={draft[part] === value}
                    onClick={() => {
                      setDraft({ ...draft, [part]: value });
                    }}
                  >
                    {t(`profile.${part}.${value}` as never)}
                  </button>
                ))}
            </div>
          </div>
        ))}
        <p className={styles['note']}>
          {t('profile.moreToUnlock')} <Link to="/collection">{t('profile.toCollection')}</Link>
        </p>
        <FormError code={error} />
        <div>
          <Button
            loading={pending}
            disabled={!changed}
            onClick={() => {
              void run(() => getAccountStore().getState().updateProfile({ avatar: draft })).then(
                (r) => {
                  if (r.ok) useToastStore.getState().push(t('profile.saved'), 'success');
                },
              );
            }}
          >
            {t('profile.save')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function AgeSection() {
  const { t } = useTranslation('account');
  const profile = useAccount((s) => s.profile);
  const { pending, error, run } = useAction();
  if (!profile) return null;
  const current = profile.ageGroup;
  const options = AGE_GROUPS.filter((g) => mayDeclare(current, g));
  return (
    <Panel title={t('age.title')}>
      <div className={styles['stack']}>
        <p>{current ? t('age.current', { group: t(AGE_LABEL[current]) }) : t('age.notDeclared')}</p>
        {current ? <p className={styles['note']}>{t('age.locked')}</p> : null}
        {options.length > 1 || current === null ? (
          <SegmentedControl
            label={t('age.title')}
            hideLabel
            value={current ?? '18_plus'}
            onChange={(g) => {
              void run(() => getAccountStore().getState().declareAge(g));
            }}
            options={options.map((g) => ({ value: g, label: t(AGE_LABEL[g]) }))}
          />
        ) : null}
        <FormError code={pending ? null : error} />
      </div>
    </Panel>
  );
}

export function ProfileScreen() {
  const { t } = useTranslation('account');
  const { available, load, snapshot } = useAccountSession();
  const profile = useAccount((s) => s.profile);

  if (!available || (load === 'ready' && snapshot.status === 'signedOut')) {
    return <Navigate to="/account" replace />;
  }
  return (
    <ScreenFrame title={t('profile.title')} backTo="/account">
      <Page>
        {profile ? (
          <>
            {/* Keyed by what was saved, so that a saved value starts the form again. */}
            <NicknameForm key={profile.nickname} />
            <AvatarEditor key={JSON.stringify(profile.avatar)} />
            <AgeSection />
          </>
        ) : (
          <Panel>
            <p className={styles['note']} role="status">
              {t('loading')}
            </p>
          </Panel>
        )}
      </Page>
    </ScreenFrame>
  );
}
